import ExpandMoreOutlined from '@mui/icons-material/ExpandMoreOutlined';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { Pill, PRIMARY_PILL_SX } from '../../Pill.js';
import { Chips, Copy } from '../primitives.js';
import { type EditableSceneField, SceneCard } from './SceneCard.js';
import type { StoryboardResult, StoryboardVersion } from './storyboardResult.js';

export function StoryboardVersionCard({
  version,
  versionKey = version.name,
  storyboard,
  editable = false,
  onSceneChange,
}: {
  version: StoryboardVersion;
  versionKey?: string | number;
  storyboard: StoryboardResult['storyboard'];
  editable?: boolean;
  onSceneChange?: (index: number, field: EditableSceneField, value: string) => void;
}): ReactNode {
  const direction = version.creative_direction;
  return (
    <Stack data-testid={`storyboard-version-${version.name}`} sx={{ gap: 3 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 340px), 1fr))',
          gap: '12px',
          alignItems: 'start',
        }}
      >
        {version.scenes.map((scene, index) => (
          <SceneCard
            key={`${versionKey}-${scene.shot_label}-${scene.start_seconds}-${scene.visual}`}
            scene={scene}
            index={index}
            elements={storyboard.elements}
            editable={editable}
            onChange={(field, value) => onSceneChange?.(index, field, value)}
          />
        ))}
      </Box>
      <Accordion
        disableGutters
        elevation={0}
        sx={{ bgcolor: 'transparent', '&:before': { display: 'none' } }}
      >
        <AccordionSummary expandIcon={<ExpandMoreOutlined />} sx={{ px: 0, minHeight: 40 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600 }}>Production details</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 0 }}>
          <Stack sx={{ gap: 3 }}>
            <Stack sx={{ gap: 2 }}>
              {version.strategy_label === '' ? null : (
                <Pill sx={{ ...PRIMARY_PILL_SX, alignSelf: 'flex-start' }}>
                  {version.strategy_label.toUpperCase()}
                </Pill>
              )}
              <Typography sx={{ fontSize: 13, textWrap: 'pretty' }}>{version.rationale}</Typography>
              <Chips values={version.display_chips} />
            </Stack>
            <Copy label="Creative premise">{version.creative_premise}</Copy>
            <Copy label="Departure from references">{version.departure_from_references}</Copy>
            <Copy label="Hero product">{direction.hero_product}</Copy>
            <Copy label="Tone & visual style">
              {[direction.tone, ...direction.visual_style].filter(Boolean).join(' · ')}
            </Copy>
            <Chips values={direction.palette} />
            <Copy label="Music direction">{direction.music}</Copy>
            <Chips values={direction.element_ids.map((id) => storyboard.elements[id]?.tag ?? id)} />
            {version.reference_ids.map((id) => {
              const reference = storyboard.references.find((entry) => entry.id === id);
              return reference === undefined ? null : (
                <Copy
                  key={id}
                  label={`Reference · ${reference.creative.creative_id}${reference.observed ? '' : ' · Not observed'}`}
                >
                  {[reference.selection_reason, reference.structure].filter(Boolean).join('\n')}
                </Copy>
              );
            })}
            <Copy label="Closing call to action">{version.cta}</Copy>
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
}
